import { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  Box,
  IconButton,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationModal from "../utils/ConfirmationModal";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import { v4 as uuidv4 } from "uuid";

// Supabase
import supabase from "../../supabaseClient";

// i18n
import { useTranslation } from "react-i18next";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function AiDatabase({ open, onClose, database, faq, groups }) {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedExcludedGroups, setSelectedExcludedGroups] = useState([]);
  const [databaseId, setDatabaseId] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [applyToUnsaved, setApplyToUnsaved] = useState(false);
  const [applyToSaved, setApplyToSaved] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const databaseNameRef = useRef(null);
  const databaseDescriptionRef = useRef(null);

  // Context
  const { teamId } = useAuth();
  const { showNotification } = useNotification();

  // i18n
  const { t } = useTranslation();

  useEffect(() => {
    if (database?.id) {
      setDatabaseId(database.id);
      setSelectedGroups(database.groups_ids);
      setSelectedExcludedGroups(database.excluded_groups_ids || []);
      const filteredFaqs = faq.filter(
        (item) => item.aidatabase_id === database.id
      );
      setFaqs(filteredFaqs || []);
      setApplyToUnsaved(database.applies_to_unsaved_contacts || false);
      setApplyToSaved(database.applies_to_saved_contacts || false);
    } else {
      setFaqs([]);
      setDatabaseId(uuidv4());
      setApplyToUnsaved(false);
      setApplyToSaved(false);
    }
  }, [database, faq]);

  const handleGroupChange = (groupId) => {
    trackEvent("aidatabase-database_change");

    if (groupId === "unsaved") {
      setApplyToUnsaved((prev) => !prev);
      return;
    } else if (groupId === "saved") {
      setApplyToSaved((prev) => !prev);
      return;
    }

    setSelectedGroups((prevSelected) => {
      if (prevSelected.includes(groupId)) {
        return prevSelected.filter((id) => id !== groupId);
      } else {
        return [...prevSelected, groupId];
      }
    });
  };

  const handleExcludedGroupChange = (groupId) => {
    trackEvent("aidatabase-excluded_group_change");
    setSelectedExcludedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSubmit = async () => {
    trackEvent("aidatabase-form_submit");
    try {
      const databaseName = databaseNameRef.current.value;
      const databaseDescription = databaseDescriptionRef.current.value;
      const { error } = await supabase.from("aidatabase").upsert([
        {
          id: databaseId,
          name: databaseName,
          description: databaseDescription,
          team_id: teamId,
          groups_ids: selectedGroups,
          excluded_groups_ids: selectedExcludedGroups,
          applies_to_unsaved_contacts: applyToUnsaved,
          applies_to_saved_contacts: applyToSaved,
        },
      ]);
      if (error) {
        throw error;
      }
      const { error: errorFaq } = await supabase.from("aifaq").upsert(faqs);

      if (errorFaq) {
        throw errorFaq;
      }

      showNotification(t("ai.success"), "success");
      resetValues();
    } catch (error) {
      showNotification(`${t("ai.error")}: ${error.message}`, "error");
    }
  };

  const handleDelete = async () => {
    trackEvent("aidatabase-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancellation = () => {
    trackEvent("aidatabase-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  const handleDeleteConfirmation = async () => {
    trackEvent("aidatabase-delete_confirmed");
    try {
      if (database) {
        const { error: deleteError } = await supabase
          .from("aidatabase")
          .delete()
          .eq("id", database.id);

        if (deleteError) {
          throw deleteError;
        }
        trackEvent("ai-delete");
        showNotification(t("ai.deleteSuccess"), "success");
        resetValues();
      }
    } catch (error) {
      showNotification(
        `${t("ai.errorDeletingDatabase")}: ${error.message}`,
        "error"
      );
    }
  };

  const resetValues = () => {
    setShowDeleteConfirmation(false);
    setSelectedGroups([]);
    setSelectedExcludedGroups([]);
    onClose();
  };

  const handleAddFaq = () => {
    if (newQuestion && newAnswer) {
      setFaqs([
        ...faqs,
        {
          id: uuidv4(),
          team_id: teamId,
          aidatabase_id: databaseId,
          question: newQuestion,
          answer: newAnswer,
        },
      ]);
      setNewQuestion("");
      setNewAnswer("");
    }
  };

  const handleDeleteFaq = async (id) => {
    // Remove it from state first (for instant feedback)
    setFaqs((prevFaqs) => prevFaqs.filter((faqs) => faqs.id !== id));

    // Try deleting from Supabase
    try {
      const { error } = await supabase.from("aifaq").delete().eq("id", id);

      if (error) {
        throw error;
      }

      showNotification(t("ai.faqDeleteSuccess"), "success");
    } catch (err) {
      showNotification(`${t("ai.errorDeletingFaq")}: ${err.message}`, "error");
    }
  };

  return (
    <Modal open={open} onClose={resetValues}>
      <Box sx={style}>
        {database && (
          <>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("ai.databaseInfo")}
            </Typography>
            <TextField
              fullWidth
              inputRef={databaseNameRef}
              label={t("ai.name")}
              defaultValue={database.name || ""}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              inputRef={databaseDescriptionRef}
              label={t("ai.description")}
              defaultValue={database.description || ""}
              sx={{ mb: 2 }}
            />

            {/* FAQ Section */}
            <Typography variant="h3" sx={{ mt: 2, mb: 2 }}>
              {t("ai.faq")}
            </Typography>
            {faqs.map((faq, index) => (
              <Accordion key={faq.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>{faq.question}</Typography>
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent accordion from toggling
                      handleDeleteFaq(faq.id);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}

            <TextField
              fullWidth
              label={t("ai.addQuestion")}
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              sx={{ mt: 2 }}
            />

            <TextField
              fullWidth
              label={t("ai.addAnswer")}
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              multiline
              rows={4}
              sx={{ mt: 2, mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleAddFaq}
              sx={{
                mb: 2,
                borderRadius: "8px",
                fontWeight: "bold",
                background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                color: "#fff",
                boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
              }}
            >
              {t("ai.addFaq")}
            </Button>

            <Typography variant="h3" sx={{ mt: 2, mb: 2 }}>
              {t("ai.broadcastLists")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ ml: 2 }}>
                {t("ai.selectedCount", { count: selectedGroups.length })}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "8px",
                  mb: 2,
                }}
              >
                <List>
                  {/* Unsaved Contacts Option */}
                  <ListItem>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={applyToSaved}
                          onChange={() => handleGroupChange("saved")}
                        />
                      }
                      label={t("ai.savedContacts")}
                    />
                  </ListItem>
                  <ListItem>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={applyToUnsaved}
                          onChange={() => handleGroupChange("unsaved")}
                        />
                      }
                      label={t("ai.unsavedContacts")}
                    />
                  </ListItem>
                  {groups.map((group) => (
                    <ListItem key={group.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedGroups.includes(group.id)}
                            onChange={() => handleGroupChange(group.id)}
                          />
                        }
                        label={group.name}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            {/* Excluded Broadcast Lists */}
            <Typography variant="h3" sx={{ mt: 2, mb: 2 }}>
              {t("ai.excludedBroadcastLists")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ ml: 2 }}>
                {t("ai.excludedSelectedCount", {
                  count: selectedExcludedGroups.length,
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "8px",
                mb: 2,
              }}
            >
              <List>
                {groups.map((group) => (
                  <ListItem key={`excluded-${group.id}`}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedExcludedGroups.includes(group.id)}
                          onChange={() => handleExcludedGroupChange(group.id)}
                        />
                      }
                      label={group.name}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box sx={{ display: "flex", mt: 4, gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  borderRadius: "8px",
                  fontWeight: "bold",
                  py: 1.5,
                  background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                  "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                }}
              >
                {database.id ? t("ai.saveChanges") : t("ai.addDatabase")}
              </Button>

              {database.id && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  fullWidth
                  sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#e2726e", borderColor: "#e2726e", py: 1.5 }}
                >
                  {t("ai.deleteDatabase")}
                </Button>
              )}
            </Box>
          </>
        )}
        <ConfirmationModal
          open={showDeleteConfirmation}
          onClose={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          title={t("ai.confirmDelete")}
          message={t("ai.confirmDeleteMessage")}
        />
      </Box>
    </Modal>
  );
}
